import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, Bot, User, ShieldOff, Clock, CheckCheck, AlertCircle, Bell, Repeat, Sparkles, Star, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";

const KIND_META = {
  manual:               { icon: User,    label: "Manual",              color: "text-orange-600" },
  quote_follow_up:      { icon: Sparkles, label: "Follow-up",           color: "text-orange-600" },
  review_request:       { icon: Star,    label: "Review request",      color: "text-emerald-600" },
  reactivation:         { icon: RotateCcw, label: "Reactivation",       color: "text-indigo-600" },
  appointment_reminder: { icon: Bell,    label: "Appointment reminder", color: "text-sky-600" },
  reengagement:         { icon: Repeat,  label: "Re-engagement",        color: "text-fuchsia-600" },
  inbound:              { icon: MessageSquare, label: "Reply",           color: "text-zinc-700" },
};

const STATUS_META = {
  queued:    { icon: Clock,       label: "Queued",    color: "text-amber-600" },
  sent:      { icon: CheckCheck,  label: "Sent",      color: "text-emerald-600" },
  delivered: { icon: CheckCheck,  label: "Delivered", color: "text-emerald-600" },
  failed:    { icon: AlertCircle, label: "Failed",    color: "text-rose-600" },
};

/**
 * SmsThread — reusable two-way SMS conversation panel.
 * Provider-agnostic: renders rows from /leads/{id}/messages or /customers/{id}/messages.
 *
 * Props:
 *   scope: "lead" | "customer"
 *   id: string  (lead id or customer id)
 *   jobId / invoiceId / estimateId: optional record IDs forwarded to
 *     TemplatePicker so merge fields like {invoice_link}, {estimate_link},
 *     {job_date} resolve against the current record (not just the customer).
 */
export default function SmsThread({ scope, id, jobId = null, invoiceId = null, estimateId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [body, setBody] = useState("");
  const listRef = useRef(null);
  const endpoint = scope === "customer" ? `/customers/${id}/messages` : `/leads/${id}/messages`;

  const load = useCallback(async (scrollToEnd = false) => {
    try {
      const { data } = await api.get(endpoint);
      setData(data);
      if (scrollToEnd) {
        setTimeout(() => {
          const el = listRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 60);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { load(true); }, [load]);

  // Poll every 20s for new inbound messages
  useEffect(() => {
    const t = setInterval(() => load(false), 20000);
    return () => clearInterval(t);
  }, [load]);

  const cust = data?.customer;
  const optedOut = !!cust?.sms_opted_out;
  const noPhone = !cust?.phone;
  const liveEffective = !!data?.sms_live_effective;

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data: m } = await api.post(endpoint, { body: body.trim() });
      setBody("");
      await load(true);
      toast.success(m?.mocked ? "Message sent (test mode)" : "Message sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp" data-testid="sms-thread">
      <div className="p-5 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">SMS thread</div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#FF5A00]"/>
            <span className="font-heading text-lg font-bold">Conversation</span>
            {cust && <span className="text-xs text-zinc-500 font-mono-accent">· {cust.phone || "no phone"}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {optedOut && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono-accent uppercase tracking-widest px-2 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200" data-testid="sms-thread-opted-out">
              <ShieldOff className="w-3 h-3"/> Opted out
            </span>
          )}
          {!liveEffective && (
            <span className="inline-flex items-center gap-1 text-[9px] font-mono-accent uppercase tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid="sms-thread-mock-badge">
              <Bot className="w-3 h-3"/> Test mode
            </span>
          )}
          {liveEffective && (
            <span className="inline-flex items-center gap-1 text-[9px] font-mono-accent uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="sms-thread-live-badge">
              <CheckCheck className="w-3 h-3"/> Live delivery
            </span>
          )}
        </div>
      </div>

      <div ref={listRef} className="p-5 space-y-3 max-h-[520px] overflow-y-auto bg-zinc-50/50" data-testid="sms-thread-messages">
        {loading && <div className="text-sm text-zinc-500 text-center py-8">Loading conversation…</div>}
        {!loading && (data?.messages || []).length === 0 && (
          <div className="text-center py-10">
            <MessageSquare className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-sm text-zinc-500">No messages yet. Send the first message below.</div>
          </div>
        )}
        {!loading && (data?.messages || []).map((m, i, arr) => {
          const prev = arr[i - 1];
          const showDay = !prev || dayLabel(m.created_at) !== dayLabel(prev.created_at);
          return (
            <div key={m.id}>
              {showDay && (
                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px bg-zinc-200"/>
                  <span className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-400">{dayLabel(m.created_at)}</span>
                  <div className="flex-1 h-px bg-zinc-200"/>
                </div>
              )}
              <Bubble msg={m}/>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="p-4 border-t border-zinc-100 bg-white">
        {optedOut ? (
          <div className="text-xs text-rose-600 flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-md p-3">
            <ShieldOff className="w-4 h-4"/>
            This customer has opted out of SMS. They must reply "START" to opt back in before you can message them again.
          </div>
        ) : noPhone ? (
          <div className="text-xs text-amber-700 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-3">
            <AlertCircle className="w-4 h-4"/>
            No phone number on file. Add one to enable SMS.
          </div>
        ) : (
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Textarea
                rows={2}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={liveEffective ? "Type a message…" : "Type a message… (test mode)"}
                className="text-sm resize-none"
                data-testid="sms-thread-composer"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
                }}
              />
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="text-[10px] font-mono-accent text-zinc-400">Cmd/Ctrl + Enter to send · Included in the same thread as automated messages</div>
                <TemplatePicker
                  channel="sms"
                  customerId={cust?.id || null}
                  jobId={jobId}
                  invoiceId={invoiceId}
                  estimateId={estimateId}
                  onPick={({ body: rendered }) => setBody(rendered)}
                  testIdPrefix="sms-thread-template-picker"
                />
              </div>
            </div>
            <Button onClick={send} disabled={sending || !body.trim()} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white h-16 px-5" data-testid="sms-thread-send-btn">
              <Send className="w-4 h-4 mr-1"/> {sending ? "…" : "Send"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Bubble({ msg }) {
  const isOut = msg.direction === "outbound";
  const isAuto = msg.origin === "automated";
  const kindMeta = KIND_META[msg.kind] || KIND_META.manual;
  const KindIcon = kindMeta.icon;
  const status = STATUS_META[msg.status] || STATUS_META.sent;
  const StatusIcon = status.icon;

  // Colors:
  //   outbound + manual   = orange (primary brand)
  //   outbound + automated = slate/dark to visually separate
  //   inbound              = white/gray on left
  const bubbleCls = isOut
    ? isAuto
      ? "bg-slate-800 text-white"
      : "bg-[#FF5A00] text-white"
    : "bg-white border border-zinc-200 text-zinc-900";

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] ${isOut ? "text-right" : "text-left"}`}>
        <div className={`inline-flex items-center gap-1.5 text-[9px] font-mono-accent uppercase tracking-widest mb-1 ${isOut ? "flex-row-reverse" : ""}`}>
          <KindIcon className={`w-3 h-3 ${isOut ? "text-white/70" : kindMeta.color}`} style={{ display: isOut ? "none" : "inline-block" }}/>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${isOut ? (isAuto ? "bg-slate-700 text-slate-100" : "bg-orange-100 text-[#E04F00]") : "bg-zinc-100 text-zinc-600"}`}>
            {isAuto ? <Bot className="w-3 h-3"/> : (isOut ? <User className="w-3 h-3"/> : <MessageSquare className="w-3 h-3"/>)}
            {isAuto ? "Automated" : (isOut ? "Sent" : "Reply")}
            {" · "}
            {kindMeta.label}
          </span>
        </div>
        <div className={`inline-block rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words shadow-sm ${bubbleCls}`} data-testid={`sms-bubble-${msg.id}`}>
          {msg.body}
        </div>
        <div className={`text-[10px] font-mono-accent text-zinc-500 mt-1 flex items-center gap-1.5 ${isOut ? "justify-end" : "justify-start"}`}>
          <span>{fmtTime(msg.created_at)}</span>
          {isOut && (
            <>
              <span>·</span>
              <span className={`inline-flex items-center gap-0.5 ${status.color}`}>
                <StatusIcon className="w-2.5 h-2.5"/>{status.label}
              </span>
              {msg.mocked && <span className="text-amber-600 uppercase">TEST</span>}
              {msg.twilio_sid && (
                <span
                  className="text-emerald-600 uppercase"
                  title={`Provider message ID: ${msg.twilio_sid}`}
                  data-testid={`sms-live-badge-${msg.id}`}
                >
                  LIVE
                </span>
              )}
              {msg.error_code && (
                <span
                  className="text-red-600 uppercase"
                  title={msg.error_message || ""}
                  data-testid={`sms-error-badge-${msg.id}`}
                >
                  ERR {msg.error_code}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const isSameDay = (a, b) => a.toDateString() === b.toDateString();
  if (isSameDay(d, now)) return "Today";
  if (isSameDay(d, y)) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: d.getFullYear() === now.getFullYear() ? undefined : "numeric" });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
